/**
 * PostHog identity for the web app (IMP-311).
 *
 * The app has mounted `<PostHogProvider>` since PostHog was introduced, but it
 * never called `identify()`. Every event therefore landed on an anonymous
 * person with no account attribution — including the `platform_*` events that
 * the shared inbox/dashboard components (RTA submodule, aliased as
 * `@shared/inbox`) already emit. This module is the ONE place that binds a
 * PostHog person to a Silia user and to their account group.
 *
 * ## Property contract
 *
 * The property names mirror the RTA widget
 * (`RTA/apps/widget/src/hooks/analytics/useAuthAnalytics.ts`) on purpose: the
 * same human uses both surfaces, and if the two disagree they show up in
 * PostHog as two differently-shaped persons that cannot be joined.
 *
 * ## PII policy
 *
 * We send exactly what the widget already sends — `email`, `name`, `role`,
 * `account_id` — and nothing more. No tokens, no permission sets, no
 * conversation or customer content. Widening this set is a deliberate decision,
 * not a convenience: PostHog persons are retained independently of our own data
 * lifecycle, so anything added here outlives the session that produced it.
 *
 * ## Fail-safe
 *
 * Every export swallows its own errors. Telemetry must never be able to throw
 * into an auth flow — a PostHog outage may cost us data, never a login. This is
 * the same rule the RTA updater follows
 * (`RTA/apps/widget/src/utils/tauri.ts`), and the seed of the single
 * `safeTrack()` entry point tracked as IMP-310.
 */
import posthog from 'posthog-js';

import { createLogger } from './logger';

const logger = createLogger('analytics');

/** Surface tag, so web events are separable from the desktop widget's. */
const SURFACE = 'web';

/**
 * Shape we accept for identification. Deliberately loose: the user object comes
 * from the login response on one path and from decoded JWT claims on the other
 * (where the backend signs the display name as `username`), so this normalizes
 * both instead of trusting one shape.
 */
export interface IAnalyticsUser {
  id?: string;
  email?: string;
  name?: string;
  username?: string;
  role?: string;
  accountId?: string;
}

/** Account group properties. `plan` mirrors the widget's group contract. */
export interface IAnalyticsAccount {
  id?: string;
  name?: string;
  plan?: string;
}

export interface IPosthogSuperProperties {
  app_version: string;
  surface: typeof SURFACE;
  env: string;
}

/**
 * Static properties attached to every event, so a chart can be filtered by
 * environment and version instead of mixing staging with prod.
 *
 * `REACT_APP_VERSION` is injected at build time from app/package.json (see
 * app/vite/craEnv.js); it is absent under Jest, hence the fallback.
 */
export function buildSuperProperties(): IPosthogSuperProperties {
  return {
    app_version: process.env.REACT_APP_VERSION?.trim() || '0.0.0',
    surface: SURFACE,
    env: process.env.REACT_APP_STAGE?.trim() || 'unknown',
  };
}

/** Registers the super properties. Safe to call when PostHog is disabled. */
export function registerSuperProperties(): void {
  try {
    posthog.register(buildSuperProperties());
  } catch {
    // PostHog may be uninitialized (no key configured) — never block bootstrap.
  }
}

/**
 * Bind the current PostHog person to this user and to their account group.
 *
 * Idempotent, so both login paths (fresh login and session restore on reload)
 * can call it. Without the restore path a returning user who cleared their
 * PostHog cookie would stay anonymous for the whole session.
 *
 * No-ops when the user has no id: identifying with `undefined` would create a
 * junk person and merge unrelated sessions into it.
 */
export function identifyUser(user: IAnalyticsUser | null | undefined): void {
  try {
    const userId = user?.id?.trim();
    if (!userId) {
      logger.debug('identify skipped: user has no id');
      return;
    }

    posthog.identify(userId, {
      email: user?.email,
      name: user?.name ?? user?.username,
      role: user?.role,
      account_id: user?.accountId,
    });

    const accountId = user?.accountId?.trim();
    if (accountId) {
      posthog.group('account', accountId);
    }
  } catch {
    // Telemetry must never break the login flow.
  }
}

/**
 * Attach (or refresh) the properties of the account group.
 *
 * Called once the account details resolve, which is later than login and may
 * target a DIFFERENT account than the user's own: an admin browsing another
 * account should have their events attributed to the account they are actually
 * looking at, which is what the `platform_*` events are about.
 */
export function setAccountGroup(account: IAnalyticsAccount | null | undefined): void {
  try {
    const accountId = account?.id?.trim();
    if (!accountId) return;

    posthog.group('account', accountId, {
      name: account?.name,
      plan: account?.plan,
    });
  } catch {
    // Telemetry must never break account loading.
  }
}

/**
 * Drop the identity on logout, so the next person on a shared browser is not
 * attributed to the previous one.
 */
export function resetIdentity(): void {
  try {
    posthog.reset();
  } catch {
    // Telemetry must never break the logout flow.
  }
}
